import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Flame, Trophy, Star, Medal, TrendingUp, 
  ChevronRight, Users, Crown, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GamificationPanel = ({ isOpen, onClose }) => {
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/user/gamification`, { headers }),
        axios.get(`${API}/leaderboard`, { headers })
      ]);
      
      setStats(statsRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (error) {
      console.error('Error loading gamification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLeagueColor = (league) => {
    const colors = {
      bronce: 'from-amber-600 to-amber-800',
      plata: 'from-slate-400 to-slate-600',
      oro: 'from-yellow-400 to-yellow-600',
      platino: 'from-cyan-400 to-cyan-600',
      diamante: 'from-purple-400 to-purple-600'
    };
    return colors[league] || colors.bronce;
  };

  const getLeagueIcon = (league) => {
    const icons = {
      bronce: '🥉',
      plata: '🥈',
      oro: '🥇',
      platino: '💎',
      diamante: '👑'
    };
    return icons[league] || '🎯';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`bg-gradient-to-r ${getLeagueColor(stats?.league || 'bronce')} text-white p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Tu Progreso</h2>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full">✕</button>
            </div>
            
            {stats && (
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-5xl mb-1">{getLeagueIcon(stats.league)}</div>
                  <p className="text-sm opacity-80 capitalize">Liga {stats.league}</p>
                </div>
                
                <div className="flex-1 grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold">{stats.level}</p>
                    <p className="text-xs opacity-80">Nivel</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold">{stats.xp}</p>
                    <p className="text-xs opacity-80">XP Total</p>
                  </div>
                  <div className="text-center flex items-center justify-center gap-1">
                    <Flame className="w-6 h-6 text-orange-300" />
                    <p className="text-3xl font-bold">{stats.streak_days}</p>
                    <p className="text-xs opacity-80">días</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200">
            {['stats', 'badges', 'leaderboard'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 text-sm font-medium capitalize ${
                  activeTab === tab
                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab === 'stats' && <TrendingUp className="w-4 h-4 inline mr-1" />}
                {tab === 'badges' && <Medal className="w-4 h-4 inline mr-1" />}
                {tab === 'leaderboard' && <Users className="w-4 h-4 inline mr-1" />}
                {tab === 'stats' ? 'Estadísticas' : tab === 'badges' ? 'Insignias' : 'Clasificación'}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-6 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <>
                {/* Stats Tab */}
                {activeTab === 'stats' && stats && (
                  <div className="space-y-6">
                    {/* XP Progress */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">Nivel {stats.level}</span>
                        <span className="text-slate-600">{stats.xp_for_next_level} XP para nivel {stats.level + 1}</span>
                      </div>
                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                          style={{ width: `${Math.min(100, ((stats.xp % 500) / 500) * 100)}%` }}
                        />
                      </div>
                    </div>

                    {/* Weekly Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-indigo-50 rounded-xl p-4">
                        <p className="text-sm text-indigo-600 mb-1">XP Esta Semana</p>
                        <p className="text-2xl font-bold text-indigo-900">{stats.weekly_xp}</p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-4">
                        <p className="text-sm text-amber-600 mb-1">Posición en Liga</p>
                        <p className="text-2xl font-bold text-amber-900">#{stats.league_rank}</p>
                      </div>
                    </div>

                    {/* Streak Info */}
                    <div className="bg-gradient-to-r from-orange-100 to-red-100 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center">
                          <Flame className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-orange-900">Racha de {stats.streak_days} días</p>
                          <p className="text-sm text-orange-700">
                            {stats.streak_days >= 7 
                              ? '¡Increíble! Sigue así.' 
                              : stats.streak_days >= 3 
                              ? '¡Buen trabajo! No pares ahora.' 
                              : 'Practica cada día para mantener tu racha.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* League Progress */}
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-3">Progreso de Liga</p>
                      <div className="flex justify-between items-center">
                        {stats.leagues?.map((league, idx) => (
                          <div 
                            key={league} 
                            className={`text-center ${league === stats.league ? 'opacity-100' : 'opacity-40'}`}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg mx-auto mb-1 ${
                              league === stats.league 
                                ? `bg-gradient-to-r ${getLeagueColor(league)} text-white` 
                                : 'bg-slate-200'
                            }`}>
                              {getLeagueIcon(league).replace(/[^\p{Emoji}]/gu, '')}
                            </div>
                            <p className="text-xs capitalize">{league}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Badges Tab */}
                {activeTab === 'badges' && stats && (
                  <div className="space-y-4">
                    {stats.badges?.length > 0 && (
                      <>
                        <h3 className="font-medium text-slate-700">Insignias Desbloqueadas</h3>
                        <div className="grid grid-cols-3 gap-3">
                          {stats.badges.map((badge, idx) => (
                            <div 
                              key={idx}
                              className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-3 text-center border border-indigo-200"
                            >
                              <div className="text-3xl mb-1">{badge.icon}</div>
                              <p className="text-sm font-medium text-slate-900">{badge.name}</p>
                              <p className="text-xs text-slate-500">{badge.description}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {stats.available_badges?.length > 0 && (
                      <>
                        <h3 className="font-medium text-slate-700 mt-6">Próximas Insignias</h3>
                        <div className="grid grid-cols-3 gap-3">
                          {stats.available_badges.map((badge, idx) => (
                            <div 
                              key={idx}
                              className="bg-slate-100 rounded-xl p-3 text-center opacity-60"
                            >
                              <div className="text-3xl mb-1 grayscale">{badge.icon}</div>
                              <p className="text-sm font-medium text-slate-700">{badge.name}</p>
                              <p className="text-xs text-slate-500">{badge.description}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Leaderboard Tab */}
                {activeTab === 'leaderboard' && leaderboard.users && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-medium text-slate-700">
                        Liga {leaderboard.league} - Top 50
                      </h3>
                      <span className="text-sm text-slate-500">
                        {leaderboard.total_in_league} participantes
                      </span>
                    </div>
                    
                    {leaderboard.users.map((user, idx) => (
                      <div 
                        key={user.user_id}
                        className={`flex items-center gap-3 p-3 rounded-xl ${
                          user.is_current_user 
                            ? 'bg-indigo-100 border-2 border-indigo-300' 
                            : 'bg-slate-50'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                          idx === 1 ? 'bg-slate-300 text-slate-700' :
                          idx === 2 ? 'bg-amber-600 text-white' :
                          'bg-slate-200 text-slate-600'
                        }`}>
                          {user.rank}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">
                            {user.name || 'Usuario'} 
                            {user.is_current_user && <span className="text-indigo-600 ml-1">(Tú)</span>}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>Nivel {user.level}</span>
                            {user.streak_days > 0 && (
                              <span className="flex items-center gap-0.5">
                                <Flame className="w-3 h-3 text-orange-500" />
                                {user.streak_days}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-indigo-600">{user.weekly_xp} XP</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GamificationPanel;
